import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '../..//hooks/useTranslation';
import type { ExportJobRecord } from '../../domains/export/models/ExportRequest';

const PROGRESS_TARGET = 80;
const PROGRESS_STEP_MIN = 5;
const PROGRESS_STEP_MAX = 15;
const PROGRESS_INTERVAL_MS = 1000;
const MIN_VISIBLE_PROGRESS = 5;

const nextProgressStep = (): number =>
  PROGRESS_STEP_MIN + Math.floor(Math.random() * (PROGRESS_STEP_MAX - PROGRESS_STEP_MIN + 1));

interface UseExportProgressOptions {
  job?: ExportJobRecord | null;
  isSaving?: boolean;
  isDownloading?: boolean;
  labels?: Partial<ExportProgressLabels>;
}

interface UseExportProgressResult {
  progress: number;
  showProgress: boolean;
  statusMessage: string | null;
  isCompleted: boolean;
  isFailed: boolean;
  isProcessing: boolean;
  hasActiveExport: boolean;
}

interface ExportProgressLabels {
  saving: string;
  processing: string;
  downloading: string;
  failedPrefix: string;
}

export function useExportProgress({
  job,
  isSaving = false,
  isDownloading = false,
  labels: labelsOverride,
}: UseExportProgressOptions): UseExportProgressResult {
  const { t } = useTranslation('common');
  const [progress, setProgress] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const labels = useMemo(
    () => ({
      saving: t('export.saving', 'Saving diagram'),
      processing: t('export.processing', 'Exporting...'),
      downloading: t('export.downloading', 'Downloading...'),
      failedPrefix: t('export.failedPrefix', 'Export failed'),
      ...labelsOverride,
    }),
    [labelsOverride, t],
  );

  const isCompleted = job?.status === 'completed';
  const isFailed = job?.status === 'failed';
  const isProcessing = job?.status === 'pending' || job?.status === 'processing';
  const hasActiveExport = isSaving || isDownloading || (job ? !isFailed : false);
  const showProgress = isSaving || isDownloading || isProcessing || isCompleted;

  const statusMessage = useMemo(() => {
    if (isSaving) return labels.saving;
    if (isDownloading || job?.status === 'completed') return labels.downloading;
    if (job?.status === 'pending' || job?.status === 'processing') return labels.processing;
    if (job?.status === 'failed') {
      return `${labels.failedPrefix}: ${job.errorMessage || 'Unknown error'}`;
    }
    return null;
  }, [isSaving, isDownloading, job, labels]);

  useEffect(() => {
    if (!showProgress) {
      setProgress(0);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    if (isFailed) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setProgress(0);
      return;
    }

    if (isCompleted || isDownloading) {
      setProgress(100);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    setProgress((prev) => (prev === 0 ? MIN_VISIBLE_PROGRESS : prev));

    if (intervalRef.current) return;

    intervalRef.current = setInterval(() => {
      setProgress((prev) => {
        const next = Math.min(PROGRESS_TARGET, prev + nextProgressStep());
        if (next >= PROGRESS_TARGET && intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        return next;
      });
    }, PROGRESS_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [showProgress, isCompleted, isDownloading, isFailed]);

  useEffect(() => {
    if ((isCompleted || isDownloading) && showProgress && !isFailed) {
      setProgress(100);
    }
  }, [isCompleted, isDownloading, isFailed, showProgress]);

  return {
    progress,
    showProgress,
    statusMessage,
    isCompleted: Boolean(isCompleted),
    isFailed: Boolean(isFailed),
    isProcessing: Boolean(isProcessing),
    hasActiveExport,
  };
}
